import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Chip,
  Menu,
} from '@mui/material';
import {
  Close as CloseIcon,
  FilterList as FilterIcon,
  CalendarToday as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { API_CONFIG, EMAIL_API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';
import { ApiThread } from './ThreadList';

// Filter interface
export interface EmailFilters {
  from_email: string[];
  to_email: string[];
  subject: string;
  body: string;
  has_attachment: boolean | null;
  date_from: string;
  date_to: string;
  is_archived: boolean | null;
}

// Date filter options
const DATE_FILTER_OPTIONS = [
  'All',
  'Today',
  'Yesterday',
  'Last 24 hrs',
  'Last 48 hrs',
  'Last 7 Days',
  'Last 30 Days',
  'This Month',
  'Last Month',
  'Custom Range',
];

interface EmailFilterModalProps {
  open: boolean;
  onClose: () => void;
  onApplyFilters: (filters: EmailFilters) => void;
  channelId: string;
  threads: ApiThread[];
  initialFilters?: EmailFilters;
  channelType?: 'email' | 'message' | 'voice';
}

const EmailFilterModal: React.FC<EmailFilterModalProps> = ({
  open,
  onClose,
  onApplyFilters,
  channelId,
  threads,
  initialFilters,
  channelType = 'email',
}) => {
  const theme = useTheme();
  
  // Filter state - initialize with initialFilters if provided
  const [filters, setFilters] = useState<EmailFilters>(
    initialFilters || {
      from_email: [],
      to_email: [],
      subject: '',
      body: '',
      has_attachment: null,
      date_from: '',
      date_to: '',
      is_archived: null,
    }
  );

  // Date filter state
  const [dateFilter, setDateFilter] = useState<string>('All');
  const [showCustomRange, setShowCustomRange] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<string>('');
  const [customEndDate, setCustomEndDate] = useState<string>('');
  const [dateFilterMenuAnchor, setDateFilterMenuAnchor] = useState<null | HTMLElement>(null);

  // Extract unique email addresses from threads for From/To dropdowns
  const [fromEmailOptions, setFromEmailOptions] = useState<string[]>([]);
  const [toEmailOptions, setToEmailOptions] = useState<string[]>([]);

  // Extract email addresses from threads
  useEffect(() => {
    if (threads && threads.length > 0) {
      // Extract from_email_id from threads (unique)
      const fromEmails = new Set<string>();
      threads.forEach((thread) => {
        if (thread.from_email_id) {
          fromEmails.add(thread.from_email_id);
        }
      });
      setFromEmailOptions(Array.from(fromEmails).sort());

      // For "To" emails, we might need to fetch from messages or use a similar approach
      // For now, we'll use the same list as from emails since we don't have direct access to "to" emails in threads
      setToEmailOptions(Array.from(fromEmails).sort());
    }
  }, [threads]);

  // Calculate number of active filters
  const getActiveFilterCount = (): number => {
    let count = 0;
    if (filters.from_email.length > 0) count++;
    if (filters.to_email.length > 0) count++;
    if (filters.subject.trim() !== '') count++;
    if (filters.body.trim() !== '') count++;
    if (filters.has_attachment !== null) count++;
    if (filters.date_from !== '' || filters.date_to !== '') count++;
    if (filters.is_archived !== null) count++;
    return count;
  };

  // Handle date filter change
  const handleDateFilterChange = (value: string) => {
    setDateFilter(value);
    
    if (value === 'All') {
      setFilters((prev) => ({
        ...prev,
        date_from: '',
        date_to: '',
      }));
      setShowCustomRange(false);
      setCustomStartDate('');
      setCustomEndDate('');
    } else if (value === 'Custom Range') {
      setShowCustomRange(true);
    } else {
      // Calculate date range based on selection
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      let dateFrom = '';
      let dateTo = '';

      switch (value) {
        case 'Today':
          dateFrom = today.toISOString().split('T')[0];
          dateTo = today.toISOString().split('T')[0];
          break;
        case 'Yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(yesterday.getDate() - 1);
          dateFrom = yesterday.toISOString().split('T')[0];
          dateTo = yesterday.toISOString().split('T')[0];
          break;
        case 'Last 24 hrs':
          const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
          dateFrom = last24Hours.toISOString().split('T')[0];
          dateTo = now.toISOString().split('T')[0];
          break;
        case 'Last 48 hrs':
          const last48Hours = new Date(now.getTime() - 48 * 60 * 60 * 1000);
          dateFrom = last48Hours.toISOString().split('T')[0];
          dateTo = now.toISOString().split('T')[0];
          break;
        case 'Last 7 Days':
          const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          dateFrom = last7Days.toISOString().split('T')[0];
          dateTo = now.toISOString().split('T')[0];
          break;
        case 'Last 30 Days':
          const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          dateFrom = last30Days.toISOString().split('T')[0];
          dateTo = now.toISOString().split('T')[0];
          break;
        case 'This Month':
          const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          dateFrom = firstDayOfMonth.toISOString().split('T')[0];
          dateTo = now.toISOString().split('T')[0];
          break;
        case 'Last Month':
          const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
          dateFrom = firstDayOfLastMonth.toISOString().split('T')[0];
          dateTo = lastDayOfLastMonth.toISOString().split('T')[0];
          break;
      }

      setFilters((prev) => ({
        ...prev,
        date_from: dateFrom,
        date_to: dateTo,
      }));
    }
  };

  // Handle custom date range apply
  const handleCustomDateRangeApply = () => {
    if (customStartDate && customEndDate) {
      setFilters((prev) => ({
        ...prev,
        date_from: customStartDate,
        date_to: customEndDate,
      }));
      setDateFilter(`Custom: ${customStartDate} to ${customEndDate}`);
      setShowCustomRange(false);
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  // Handle reset all filters - immediately apply reset filters to update active filter count
  const handleResetFilters = () => {
    // Create reset filters object
    const resetFilters: EmailFilters = {
      from_email: [],
      to_email: [],
      subject: '',
      body: '',
      has_attachment: null,
      date_from: '',
      date_to: '',
      is_archived: null,
    };
    
    // Update local state
    setFilters(resetFilters);
    setDateFilter('All');
    setShowCustomRange(false);
    setCustomStartDate('');
    setCustomEndDate('');
    
    // Immediately apply reset filters to parent component to update active filter count
    // Keep modal open so user can set new filters if needed
    onApplyFilters(resetFilters);
  };

  // Handle apply filters
  const handleApplyFilters = () => {
    onApplyFilters(filters);
    onClose();
  };

  // Helper function to detect which preset date range was used from date values
  // This matches the exact logic used in handleDateFilterChange
  const detectDateFilterPreset = (dateFrom: string, dateTo: string): string => {
    if (!dateFrom || !dateTo) {
      return 'All';
    }

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const todayStr = today.toISOString().split('T')[0];

    // Check if it's a single day (same date)
    if (dateFrom === dateTo) {
      // Check if it's today
      if (dateFrom === todayStr) {
        return 'Today';
      }
      // Check if it's yesterday
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      if (dateFrom === yesterdayStr) {
        return 'Yesterday';
      }
    }

    // For ranges, calculate expected dates using the same logic as handleDateFilterChange
    // Check Last 24 hrs - dateTo should be today, dateFrom should be the date 24 hours ago
    const last24Hours = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const last24HoursStr = last24Hours.toISOString().split('T')[0];
    if (dateFrom === last24HoursStr && dateTo === todayStr) {
      return 'Last 24 hrs';
    }

    // Check Last 48 hrs
    const last48Hours = new Date(now.getTime() - 48 * 60 * 60 * 1000);
    const last48HoursStr = last48Hours.toISOString().split('T')[0];
    if (dateFrom === last48HoursStr && dateTo === todayStr) {
      return 'Last 48 hrs';
    }

    // Check Last 7 Days
    const last7Days = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const last7DaysStr = last7Days.toISOString().split('T')[0];
    if (dateFrom === last7DaysStr && dateTo === todayStr) {
      return 'Last 7 Days';
    }

    // Check Last 30 Days
    const last30Days = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const last30DaysStr = last30Days.toISOString().split('T')[0];
    if (dateFrom === last30DaysStr && dateTo === todayStr) {
      return 'Last 30 Days';
    }

    // Check This Month
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const firstDayOfMonthStr = firstDayOfMonth.toISOString().split('T')[0];
    if (dateFrom === firstDayOfMonthStr && dateTo === todayStr) {
      return 'This Month';
    }

    // Check Last Month
    const firstDayOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDayOfLastMonth = new Date(now.getFullYear(), now.getMonth(), 0);
    const firstDayOfLastMonthStr = firstDayOfLastMonth.toISOString().split('T')[0];
    const lastDayOfLastMonthStr = lastDayOfLastMonth.toISOString().split('T')[0];
    if (dateFrom === firstDayOfLastMonthStr && dateTo === lastDayOfLastMonthStr) {
      return 'Last Month';
    }

    // If no preset matches, it's a custom range
    return `Custom: ${dateFrom} to ${dateTo}`;
  };

  // Initialize filters when modal opens or initialFilters change
  useEffect(() => {
    if (open) {
      if (initialFilters) {
        setFilters(initialFilters);
        // Update date filter display based on initial filters
        if (initialFilters.date_from && initialFilters.date_to) {
          const detectedPreset = detectDateFilterPreset(initialFilters.date_from, initialFilters.date_to);
          setDateFilter(detectedPreset);
        } else {
          setDateFilter('All');
        }
      } else {
        // Reset to default state when no initial filters provided
        setFilters({
          from_email: [],
          to_email: [],
          subject: '',
          body: '',
          has_attachment: null,
          date_from: '',
          date_to: '',
          is_archived: null,
        });
        setDateFilter('All');
        setShowCustomRange(false);
        setCustomStartDate('');
        setCustomEndDate('');
      }
    }
  }, [open, initialFilters]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          fontWeight: 600,
        }}
      >
        {channelType === 'message' ? 'Message Filters' : 'Email Filters'}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            onClick={handleResetFilters}
            variant="outlined"
            color="error"
            size="small"
            sx={{
              textTransform: 'none',
              fontSize: '0.75rem',
            }}
          >
            Reset All Filters
          </Button>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.error.main,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {/* From Email - Multi-select - Hide for message channels */}
          {channelType === 'email' && (
            <Autocomplete
              multiple
              options={fromEmailOptions}
              value={filters.from_email}
              onChange={(event, newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  from_email: newValue,
                }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="From"
                  placeholder="Select from email"
                  InputLabelProps={{
                    shrink: filters.from_email.length > 0,
                  }}
                />
              )}
            />
          )}

          {/* To Email - Multi-select - Hide for message channels */}
          {channelType === 'email' && (
            <Autocomplete
              multiple
              options={toEmailOptions}
              value={filters.to_email}
              onChange={(event, newValue) => {
                setFilters((prev) => ({
                  ...prev,
                  to_email: newValue,
                }));
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option}
                    {...getTagProps({ index })}
                    key={option}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="To"
                  placeholder="Select to email"
                  InputLabelProps={{
                    shrink: filters.to_email.length > 0,
                  }}
                />
              )}
            />
          )}

          {/* Subject/Thread Name - Text field - Change label for message channels */}
          <TextField
            fullWidth
            label={channelType === 'message' ? 'Thread Name' : 'Subject'}
            value={filters.subject}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                subject: e.target.value,
              }))
            }
            placeholder={channelType === 'message' ? 'Type thread name here...' : 'Type subject here...'}
            size="small"
          />

          {/* Includes the words - Text field */}
          <TextField
            fullWidth
            label="Includes the words"
            value={filters.body}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                body: e.target.value,
              }))
            }
            placeholder="eg. features"
            size="small"
          />

          {/* Date Within - Date filter with Menu */}
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                color: 'text.secondary',
                fontSize: '0.75rem',
              }}
            >
              Date Within
            </Typography>
            <Button
              variant="outlined"
              onClick={(e) => setDateFilterMenuAnchor(e.currentTarget)}
              size="small"
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                borderColor: theme.palette.divider,
                color: 'text.primary',
              }}
              startIcon={<CalendarIcon />}
              endIcon={<ArrowDownIcon />}
            >
              {dateFilter}
            </Button>
            <Menu
              anchorEl={dateFilterMenuAnchor}
              open={Boolean(dateFilterMenuAnchor)}
              onClose={() => {
                setDateFilterMenuAnchor(null);
                setShowCustomRange(false);
                setCustomStartDate('');
                setCustomEndDate('');
              }}
              PaperProps={{
                sx: {
                  minWidth: '200px',
                  p: showCustomRange ? 0 : 1,
                },
              }}
            >
              {showCustomRange ? (
                <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    fullWidth
                    size="small"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <CalendarIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => {
                        setShowCustomRange(false);
                        setCustomStartDate('');
                        setCustomEndDate('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        handleCustomDateRangeApply();
                        setDateFilterMenuAnchor(null);
                      }}
                      disabled={!customStartDate || !customEndDate}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>
              ) : (
                // MUI Menu doesn't accept Fragment, so we use an array of MenuItems
                [
                  ...DATE_FILTER_OPTIONS.filter(opt => opt !== 'Custom Range').map((option) => (
                    <MenuItem
                      key={option}
                      onClick={() => {
                        handleDateFilterChange(option);
                        setDateFilterMenuAnchor(null);
                      }}
                    >
                      {option}
                    </MenuItem>
                  )),
                  <MenuItem
                    key="custom-range"
                    onClick={() => {
                      setShowCustomRange(true);
                    }}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    Custom Range
                    <ArrowDownIcon fontSize="small" />
                  </MenuItem>
                ]
              )}
            </Menu>
          </Box>

          {/* Has Attachments - Checkbox - Hide for message channels */}
          {channelType === 'email' && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={filters.has_attachment === true}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      has_attachment: e.target.checked ? true : null,
                    }))
                  }
                  size="small"
                />
              }
              label="Has Attachments"
            />
          )}

          {/* Archived - Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={filters.is_archived === true}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    is_archived: e.target.checked ? true : null,
                  }))
                }
                size="small"
              />
            }
            label="Archived"
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleApplyFilters} variant="contained" color="primary">
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailFilterModal;
